import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getBlogs } from "../../api/blog";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import Loader from "../../components/ui/Loader";
import {
  Search,
  Calendar,
  User,
  Heart,
  MessageCircle,
  Share2,
  BookOpen,
  TrendingUp,
  Sparkles,
  ChevronRight,
  ArrowRight,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedBackground from "../../components/ui/AnimatedBackground";

export default function BlogList() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        const data = await getBlogs(page, 12);
        setBlogs(data.blogs);
        setTotalPages(data.pagination.pages);

        if (page === 1 && data.blogs.length > 0) {
          setFeaturedBlogs(data.blogs.slice(0, 3));
        }
      } catch (error) {
        console.error("Failed to fetch blogs", error);
        toast.error("Failed to load blogs");
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, [page]);

  if (loading) return <Loader />;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <div className="min-h-screen bg-transparent relative overflow-hidden">
      <AnimatedBackground />
      {/* Dynamic Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
          className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-indigo-500/10 blur-[120px]" 
        />
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 3, repeat: Infinity, repeatType: "reverse", delay: 1 }}
          className="absolute bottom-[20%] right-[-5%] w-[30%] h-[30%] rounded-full bg-purple-500/10 blur-[100px]" 
        />
      </div>

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center space-y-8"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm"
            >
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-[10px] font-black tracking-widest uppercase text-primary">
                Explore The Future
              </span>
            </motion.div>
            
            <h1 className="text-5xl md:text-7xl lg:text-9xl font-black tracking-tighter leading-[0.85] text-foreground">
              Think <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">Bigger</span>.
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-medium leading-relaxed">
              Insights, trends, and hacks to level up your career. Curated by experts, designed for the ambitious.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Section */}
      {page === 1 && featuredBlogs.length > 0 && (
        <section className="pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex items-center justify-between mb-10"
            >
              <h2 className="text-2xl font-black flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-orange-500" />
                Featured Stories
              </h2>
              <div className="hidden sm:block h-[1px] flex-1 mx-8 bg-gradient-to-r from-border via-transparent to-transparent" />
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 h-full">
              {/* Main Featured */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-8 group"
              >
                <Link to={`/blog/${featuredBlogs[0].id}`}>
                  <Card className="h-full overflow-hidden border-0 shadow-2xl relative group-hover:translate-y-[-4px] transition-all duration-500">
                    <div className="aspect-[16/9] lg:aspect-auto lg:h-[500px] overflow-hidden relative">
                      {featuredBlogs[0].coverImage ? (
                        <img src={featuredBlogs[0].coverImage} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="" />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center"><BookOpen className="w-20 h-20 text-muted-foreground/20" /></div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                      <div className="absolute bottom-0 left-0 p-8 sm:p-12 space-y-4">
                        <Badge className="bg-primary text-white font-black px-4 py-1.5 rounded-lg uppercase tracking-wider text-[10px]">Must Read</Badge>
                        <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-[1.1] line-clamp-2">{featuredBlogs[0].title}</h3>
                        <div className="flex items-center gap-6 text-white/80">
                           <span className="flex items-center gap-2 font-bold"><Heart className="w-5 h-5 fill-rose-500 text-rose-500" /> {featuredBlogs[0]._count?.likes || 0}</span>
                           <span className="flex items-center gap-2 font-bold"><MessageCircle className="w-5 h-5 fill-blue-500 text-blue-500" /> {featuredBlogs[0]._count?.comments || 0}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>

              {/* Side Featured */}
              <div className="lg:col-span-4 flex flex-col gap-8">
                {featuredBlogs.slice(1, 3).map((blog, idx) => (
                  <motion.div
                    key={blog.id}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: idx * 0.2 }}
                    className="flex-1"
                  >
                    <Link to={`/blog/${blog.id}`} className="group h-full block">
                      <Card className="h-full overflow-hidden border-0 shadow-xl group-hover:translate-y-[-4px] transition-all duration-500">
                        <div className="aspect-[16/9] overflow-hidden relative">
                          {blog.coverImage ? (
                            <img src={blog.coverImage} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="" />
                          ) : (
                            <div className="w-full h-full bg-muted flex items-center justify-center"><BookOpen className="w-10 h-10 text-muted-foreground/20" /></div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                          <div className="absolute bottom-0 left-0 p-6 space-y-2">
                            <h4 className="text-lg font-black text-white line-clamp-2">{blog.title}</h4>
                            <span className="text-xs text-white/60 font-medium font-bold uppercase tracking-widest flex items-center gap-2">Read Article <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" /></span>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Grid List */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-between mb-10"
          >
            <h2 className="text-2xl font-black flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-indigo-500" />
              Latest Feed
            </h2>
          </motion.div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
          >
            {blogs.map((blog) => (
              <motion.div key={blog.id} variants={itemVariants}>
                <Link to={`/blog/${blog.id}`} className="group flex flex-col h-full">
                  <Card className="flex flex-col h-full overflow-hidden border border-border shadow-sm hover:shadow-2xl hover:border-primary/20 transition-all duration-500 bg-card/40 backdrop-blur-md group-hover:translate-y-[-4px]">
                    <div className="aspect-[16/10] overflow-hidden relative">
                      {blog.coverImage ? (
                        <img src={blog.coverImage} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="" />
                      ) : (
                        <div className="w-full h-full bg-muted/30 flex items-center justify-center text-muted-foreground/30"><BookOpen className="w-12 h-12" /></div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>

                    <CardContent className="p-6 flex flex-col flex-grow space-y-4">
                      <div className="flex items-center gap-3 text-xs text-muted-foreground font-bold">
                        <span className="px-2 py-0.5 rounded bg-muted/60 text-[10px] uppercase">{format(new Date(blog.createdAt), "MMM d")}</span>
                        {blog.author && <span className="truncate flex items-center gap-1"><User className="w-3 h-3" /> {blog.author.email.split("@")[0]}</span>}
                      </div>

                      <h3 className="text-lg font-black leading-tight text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        {blog.title}
                      </h3>

                      <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed flex-grow">
                        {blog.content}
                      </p>

                      <div className="flex items-center justify-between pt-4 border-t border-border/50">
                        <div className="flex items-center gap-4">
                           <span className="flex items-center gap-1.5 text-xs font-black text-rose-500"><Heart className="w-3.5 h-3.5" /> {blog._count?.likes || 0}</span>
                           <span className="flex items-center gap-1.5 text-xs font-black text-blue-500"><MessageCircle className="w-3.5 h-3.5" /> {blog._count?.comments || 0}</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-primary transition-all group-hover:translate-x-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          {/* Pagination */}
          <AnimatePresence mode="wait">
            {totalPages > 1 && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex justify-center gap-4 mt-16"
              >
                <Button variant="outline" className="h-12 px-8 rounded-xl font-bold transition-all hover:bg-primary hover:text-white" onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo(0, 0); }} disabled={page === 1}>Previous</Button>
                <div className="flex items-center px-6 bg-muted/40 rounded-xl font-black text-sm tracking-widest uppercase">Page {page} / {totalPages}</div>
                <Button variant="outline" className="h-12 px-8 rounded-xl font-bold transition-all hover:bg-primary hover:text-white" onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo(0, 0); }} disabled={page === totalPages}>Next</Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}
